import { useEffect, useState } from "react";

import { useAuth } from "@/contexts/AuthContext";
import { type Customer, type Store, StoreService } from "@/services/features/StoreService";
import { getCurrentRegion } from "@/utils/environment";
import { UserRole } from "@/utils/roleManager";
import { readSessionCache, writeSessionCache } from "@/utils/sessionCache";

type StoreInsightsLookupCache = {
  customers: Customer[];
  stores: Store[];
  includesCustomers: boolean;
};

const STORE_INSIGHTS_LOOKUP_CACHE_VERSION = 1;

const getStoreInsightsLookupCacheKey = (userId?: number) =>
  `storeInsights.lookups.v${STORE_INSIGHTS_LOOKUP_CACHE_VERSION}.${getCurrentRegion()}.${userId ?? "anonymous"}`;

const isStoreInsightsLookupCache = (value: unknown): value is StoreInsightsLookupCache => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;

  const cache = value as Record<string, unknown>;
  return (
    Array.isArray(cache.customers) &&
    Array.isArray(cache.stores) &&
    typeof cache.includesCustomers === "boolean"
  );
};

const isDemoStore = (store: Store) => {
  const name = store.name.toLowerCase();
  const group = store.group.toLowerCase();
  return name.includes("demo") || group.includes("demo");
};

export const useStoreInsightsLookups = () => {
  const { user, permissions } = useAuth();
  const isCraveUser = permissions?.isCraveUser?.() ?? false;
  const cacheKey = getStoreInsightsLookupCacheKey(user?.id);
  const [initialCache] = useState<StoreInsightsLookupCache | null>(() => {
    const cached = readSessionCache({
      key: cacheKey,
      version: STORE_INSIGHTS_LOOKUP_CACHE_VERSION,
      isValidValue: isStoreInsightsLookupCache,
    });

    return cached?.includesCustomers === isCraveUser ? cached : null;
  });
  const [stores, setStores] = useState<Store[]>(() => initialCache?.stores ?? []);
  const [customers, setCustomers] = useState<Customer[]>(() => initialCache?.customers ?? []);
  const [loading, setLoading] = useState(() => initialCache === null);

  useEffect(() => {
    if (initialCache) return;

    const cached = readSessionCache({
      key: cacheKey,
      version: STORE_INSIGHTS_LOOKUP_CACHE_VERSION,
      isValidValue: isStoreInsightsLookupCache,
    });
    if (cached?.includesCustomers === isCraveUser) {
      setCustomers(cached.customers);
      setStores(cached.stores);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const storeService = StoreService.getInstance();

    const load = async () => {
      try {
        let nextCustomers: Customer[] = [];
        if (isCraveUser) {
          try {
            const customerResponse = await storeService.getCustomers();
            nextCustomers = customerResponse.data ?? [];
          } catch {
            // Store lookups remain available when the customer endpoint is unavailable.
          }
        }

        const storeResponse = await storeService.getStores();
        if (cancelled) return;

        const nextStores = storeResponse.data ?? [];
        setCustomers(nextCustomers);
        setStores(nextStores);
        writeSessionCache(cacheKey, STORE_INSIGHTS_LOOKUP_CACHE_VERSION, {
          customers: nextCustomers,
          stores: nextStores,
          includesCustomers: isCraveUser,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [cacheKey, initialCache, isCraveUser]);

  const isCraveAdmin = user?.role === UserRole.CRAVE_ADMIN;
  const visibleStores = isCraveAdmin ? stores : stores.filter((store) => !isDemoStore(store));

  return { user, isCraveUser, stores: visibleStores, customers, loading };
};
